/**
 * AgentMarkdown — Rendu markdown unifié pour les sorties d'agents IA.
 *
 * Utilisé par tous les panels agents (AG-02, AG-03, AG-04) pour que les
 * `analysis` / rapports markdown soient rendus en HTML stylé (titres, listes,
 * tableaux, gras) au lieu d'être affichés en brut avec ## et |.
 *
 * Props :
 *   - children (string) : contenu markdown à rendre
 *   - className (string, optional) : classes appliquées au wrapper
 *   - density ("normal" | "compact") : ajuste les marges (compact pour panels)
 */
import ReactMarkdown from "react-markdown";

const COMPONENTS_BASE = {
  h1: ({ node, ...props }) => (
    <h1 className="text-base font-medium mt-3 mb-1.5" style={{ color: "var(--oh-text)" }} {...props} />
  ),
  h2: ({ node, ...props }) => (
    <h2 className="text-sm font-medium mt-3 mb-1.5" style={{ color: "var(--oh-text)" }} {...props} />
  ),
  h3: ({ node, ...props }) => (
    <h3 className="text-sm font-medium mt-2.5 mb-1" style={{ color: "var(--oh-text)" }} {...props} />
  ),
  p: ({ node, ...props }) => (
    <p className="my-1.5 leading-relaxed" {...props} />
  ),
  ul: ({ node, ...props }) => (
    <ul className="list-disc pl-5 my-1.5 space-y-0.5" {...props} />
  ),
  ol: ({ node, ...props }) => (
    <ol className="list-decimal pl-5 my-1.5 space-y-0.5" {...props} />
  ),
  li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />,
  strong: ({ node, ...props }) => (
    <strong className="font-medium" style={{ color: "var(--oh-text)" }} {...props} />
  ),
  em: ({ node, ...props }) => <em className="italic" {...props} />,
  code: ({ node, inline, ...props }) =>
    inline ? (
      <code
        className="px-1 py-0.5 rounded text-[0.85em] font-mono"
        style={{ background: "var(--oh-bg-base)", color: "var(--oh-text)" }}
        {...props}
      />
    ) : (
      <code className="block p-2 rounded font-mono text-xs whitespace-pre"
        style={{ background: "var(--oh-bg-base)", color: "var(--oh-text)" }}
        {...props}
      />
    ),
  pre: ({ node, ...props }) => (
    <pre className="my-2 overflow-x-auto" {...props} />
  ),
  table: ({ node, ...props }) => (
    <div className="my-2 overflow-x-auto oh-responsive-table">
      <table className="min-w-full text-xs border-collapse" {...props} />
    </div>
  ),
  thead: ({ node, ...props }) => (
    <thead style={{ background: "var(--oh-bg-base)" }} {...props} />
  ),
  th: ({ node, ...props }) => (
    <th className="px-2 py-1 text-left font-medium border"
      style={{ borderColor: "var(--oh-border)" }} {...props} />
  ),
  td: ({ node, ...props }) => (
    <td className="px-2 py-1 border" style={{ borderColor: "var(--oh-border)" }} {...props} />
  ),
  a: ({ node, ...props }) => (
    <a className="underline" style={{ color: "var(--oh-stat-blue-text)" }}
       target="_blank" rel="noopener noreferrer" {...props} />
  ),
  hr: ({ node, ...props }) => (
    <hr className="my-2" style={{ borderColor: "var(--oh-border)" }} {...props} />
  ),
};

export default function AgentMarkdown({ children, className = "", density = "normal" }) {
  if (!children) return null;
  const text = typeof children === "string" ? children : String(children);
  const wrapperCls = density === "compact"
    ? `text-xs leading-relaxed ${className}`
    : `text-sm leading-relaxed ${className}`;
  return (
    <div className={wrapperCls} data-testid="agent-markdown">
      <ReactMarkdown components={COMPONENTS_BASE}>{text}</ReactMarkdown>
    </div>
  );
}
